import React, { useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import {
  createExportHeader,
  createExportRows,
  createExportColumns,
  createExportHeaderRows,
  getDateRangeString
} from "./export-helpers";
import "./export-report-modal.scss";
import IlReportLogo from "./il-report-logo";
import ExportButton from "../export-button";
import { IconClose, IconDownload, IconPrint } from "../icons";
import html2PDF from "jspdf-html2canvas";
import PrintButton from "../printbutton/printbutton";

const debugMode = false;
const defaultPdfMargin = 92;

const ExportReportModal = (props) => {
  const {
    instructor,
    schoolYear,
    dateRange,
    subject,
    title,
    breadcrumb,
    grade,
    subtitle,
    onClose,
    table,
    data,
    fileName = "mypath_export",
    reportName,
    pageSize = [1280, 720],
    csvCallback,
    includePrintButton = false,
    orientation = "landscape",
    mpngStyle = true
  } = props;
  const currentDate = new Date();
  const currentDayOfMonth = currentDate.getDate();
  const currentMonth = currentDate.getMonth(); // Be careful! January is 0, not 1
  const currentYear = currentDate.getFullYear();
  const todaysDate = currentMonth + 1 + "/" + currentDayOfMonth + "/" + currentYear;
  const [currentAction, setCurrentAction] = useState("");
  const ref = useRef(null);
  const finalFileName = reportName ?? `${fileName}${title ? "_" + title : ""}_${todaysDate.split("/").join("_")}`;
  const handleClickDownload = async (callbackData) => {
    const newData = callbackData ?? data;
    const content =
      (newData.columns ? createExportColumns(newData) : createExportHeader(newData)) +
      (newData.headerGroups ? createExportHeaderRows(newData) : createExportRows(newData));
    const a = document.createElement("a");
    a.textContent = "download";
    a.download = finalFileName + ".csv";
    a.href = "data:text/csv;charset=utf-8,%EF%BB%BF" + encodeURIComponent(content);
    a.click();
    return true;
  };
  const handleClickExportPdf = async () => {
    const margin =
      orientation === "portrait"
        ? { top: 20, left: 20, right: 20, bottom: 20 }
        : {
            top: defaultPdfMargin - 20,
            left: defaultPdfMargin,
            right: defaultPdfMargin,
            bottom: defaultPdfMargin - 20
          };
    const element = ref.current;
    if (!element) return;
    await html2PDF(element, {
      jsPDF: {
        orientation: orientation,
        unit: "px",
        format: "letter",
        putOnlyUsedFonts: true
      },
      html2canvas: { scale: 3 },
      imageType: "image/jpeg",
      margin: margin,
      output: finalFileName + ".pdf"
    });
  };

  const handleExport = async (action) => {
    if (action === "csv") {
      !csvCallback && (await handleClickDownload());
      csvCallback &&
        csvCallback(async (data) => {
          await handleClickDownload(data);
        });
    }
    if (action === "pdf") {
      setCurrentAction(action);
      !debugMode &&
        setTimeout(async () => {
          await handleClickExportPdf();
          setCurrentAction("");
        }, 1000); // required so it can finish rendering
    }
  };

  let style = {};
  if (debugMode) {
    style = { top: 0 };
  }

  const handleCloseButton = () => {
    setCurrentAction("");
    onClose();
  };

  const reportFilter = ({ label, value }) => {
    return (
      <span>
        <b>{label}:</b> {value}
      </span>
    );
  };

  const filters = [
    { label: "Grade", value: grade },
    { label: "Instructor", value: instructor },
    { label: "Subject", value: subject },
    { label: "Date Range", value: getDateRangeString(dateRange) },
    { label: "School Year", value: schoolYear }
  ];

  return (
    <>
      {includePrintButton && <PrintButton mpngStyle={mpngStyle} />}
      <ExportButton {...props} mpngStyle={mpngStyle} onExport={handleExport} />
      {(currentAction || includePrintButton) && (
        <div className="csr-export-report-modal" data-testid="csr-export-report-modal-container" style={style}>
          <div className={`export-report-modal`}>
            <div className="export-report-modal-header">
              <div className="row">
                <h4 className="col-11 header-text" data-testid="export-report-modal-header">
                  Export Report
                </h4>
                <div className="col-1 align-right" aria-label="Close Button">
                  <Button variant="outline-dark no-outline" onClick={handleCloseButton} className="modal-close-button">
                    <IconClose />
                  </Button>
                </div>
              </div>
            </div>
            <div className="export-report-modal-content">
              <div className="export-actions">
                <Button
                  aria-label="Print"
                  variant="outline-primary"
                  onClick={handleClickExportPdf}
                  className="export-action-button"
                  tabIndex={0}
                >
                  <IconPrint /> Print
                </Button>
                <Button
                  aria-label="Download PDF"
                  variant="outline-primary"
                  onClick={handleClickDownload}
                  className="export-action-button"
                  tabIndex={0}
                >
                  <IconDownload /> Download
                </Button>
              </div>

              <div className="export-modal-border-container">
                <div className="export-report-modal-container" ref={ref} style={{ width: `${pageSize[0]}px` }}>
                  <div className="export-modal-header">
                    <div className="export-modal-left-column">
                      <h1 className="report-name">{title}</h1>
                      <h4 className="report-subheader">{subtitle}</h4>
                      <div className="report-cucumber">{breadcrumb}</div>
                      <div className="report-filters">
                        {filters
                          .filter((f) => !!f.value)
                          .map((filter, index) => {
                            if (index) {
                              return (
                                <>
                                  {" | "}
                                  {reportFilter(filter)}
                                </>
                              );
                            } else {
                              return reportFilter(filter);
                            }
                          })}
                      </div>
                    </div>
                    <div className="export-modal-right-column">
                      <div className="logo-container">
                        <IlReportLogo className="report-il-logo" />
                      </div>
                      <div className="modal-day-date">
                        <b>Created on:</b> {todaysDate}
                      </div>
                    </div>
                  </div>
                  <Row>
                    <Col md={12}>
                      <div className="separator-line"></div>
                    </Col>
                  </Row>
                  <div className="report-information">{table}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExportReportModal;
